import { motion } from "framer-motion";
import { Phone, MessageCircle, MapPin } from "lucide-react";
import { BUSINESS } from "@/lib/siteData";

const HERO_BG =
    "https://images.unsplash.com/photo-1613977257363-707ba9348227?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBsdXh1cnklMjBob3VzZXxlbnwwfHx8fDE3ODg1MTE5MTJ8MA&ixlib=rb-4.1.0&q=85";

const fadeUp = (delay = 0) => ({
    initial: { opacity: 0, y: 24 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, delay, ease: "easeOut" },
});

const Hero = () => (
    <section id="top" className="relative overflow-hidden pt-16">
        <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${HERO_BG})` }}
            aria-hidden="true"
        />
        <div className="absolute inset-0 hero-overlay" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-20 sm:py-28">
            <motion.p
                {...fadeUp(0)}
                className="text-xs font-mono2 uppercase tracking-[0.3em] text-amber-400/90"
            >
                Gurugram • Haryana • Real Estate Consultancy
            </motion.p>
            <motion.h1
                {...fadeUp(0.12)}
                data-testid="hero-title"
                className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-50 leading-tight"
            >
                SHUBHAM <span className="text-amber-400">HOUSING</span>
            </motion.h1>
            <motion.div {...fadeUp(0.22)}>
                <span
                    data-testid="hero-tagline-badge"
                    className="mt-6 inline-flex items-center gap-2 rounded-full border border-amber-500/40 bg-amber-400/10 px-5 py-2 text-sm font-semibold tracking-widest text-amber-300 uppercase"
                >
                    Sale <span className="text-amber-500/60">•</span> Rent{" "}
                    <span className="text-amber-500/60">•</span> Purchase
                </span>
            </motion.div>
            <motion.p
                {...fadeUp(0.3)}
                className="mt-6 max-w-xl text-base leading-relaxed text-slate-300"
            >
                Your trusted partner for buying, selling and renting property in
                Gurgaon. Straightforward advice, verified options and deals
                closed with complete transparency.
            </motion.p>

            <motion.div
                {...fadeUp(0.4)}
                data-testid="hero-director-card"
                className="mt-8 inline-flex items-center gap-4 rounded-2xl border border-amber-500/20 bg-[#16203B]/80 backdrop-blur px-5 py-4"
            >
                <div className="w-12 h-12 rounded-full bg-amber-400/15 border border-amber-500/40 flex items-center justify-center font-display text-xl font-bold text-amber-300">
                    SM
                </div>
                <div>
                    <p className="font-display text-lg font-semibold text-slate-100 leading-tight">
                        {BUSINESS.director}
                    </p>
                    <p className="text-xs font-mono2 uppercase tracking-[0.2em] text-amber-400/80">
                        {BUSINESS.role}
                    </p>
                </div>
            </motion.div>

            <motion.div {...fadeUp(0.5)} className="mt-8 flex flex-wrap items-center gap-3">
                <a
                    href={BUSINESS.tel1}
                    data-testid="hero-call-primary-button"
                    aria-label={`Call ${BUSINESS.phone1Display}`}
                    className="inline-flex items-center gap-2 rounded-full bg-amber-400 text-[#0B132B] px-6 py-3 text-sm font-bold hover:bg-amber-300 transition-colors"
                >
                    <Phone size={16} /> {BUSINESS.phone1Display}
                </a>
                <a
                    href={BUSINESS.tel2}
                    data-testid="hero-call-secondary-button"
                    aria-label={`Call ${BUSINESS.phone2Display}`}
                    className="inline-flex items-center gap-2 rounded-full border border-amber-500/50 text-amber-300 px-6 py-3 text-sm font-bold hover:bg-amber-400/10 transition-colors"
                >
                    <Phone size={16} /> {BUSINESS.phone2Display}
                </a>
                <a
                    href={BUSINESS.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="hero-whatsapp-chat-button"
                    aria-label="Chat with Shubham Housing on WhatsApp"
                    className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-[#062b16] px-6 py-3 text-sm font-bold hover:bg-[#1EBE57] transition-colors"
                >
                    <span className="relative flex">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-white/40 animate-ping" />
                        <MessageCircle size={16} className="relative" />
                    </span>
                    WhatsApp Us
                </a>
            </motion.div>

            <motion.p
                {...fadeUp(0.6)}
                className="mt-8 flex items-start gap-2 text-sm text-slate-400"
            >
                <MapPin size={15} className="mt-0.5 text-amber-400 shrink-0" />
                {BUSINESS.addressLines.join(", ")}
            </motion.p>
        </div>
    </section>
);

export default Hero;
